import React, { useState, useEffect } from 'react';
import {
    FaSearch,
    FaFilter,
    FaDownload,
    FaBox,
    FaStore,
    FaExclamationTriangle,
    FaCheckCircle,
    FaTimesCircle,
    FaEye,
    FaEdit,
    FaTrash,
    FaArrowUp,
    FaArrowDown,
    FaPlus,
    FaClock,
    FaSpinner
} from 'react-icons/fa';
import { productService, categoryService, authService } from '../../../../services/apiService';
import AdminLocationDropdown from '../components/AdminLocationDropdown';
import { toast } from 'react-toastify';

const Products = () => {
    const [view, setView] = useState('list'); // 'list', 'view_detail'
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editForm, setEditForm] = useState({
        name: '',
        sku: '',
        category: '',
        price: 0,
        stock: 0,
        status: 'pending',
        isActive: false,
        description: ''
    });
    const [savingEdit, setSavingEdit] = useState(false);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all'); // 'pending', 'approved', 'all' - Default to show all products
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(() => {
        try {
            const raw = localStorage.getItem('adminLocation');
            if (!raw) return '';
            try { return JSON.parse(raw); } catch (e) { return raw; }
        } catch (e) { return ''; }
    });
    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    useEffect(() => {
        setSelectedProductIds(prev => prev.filter(id => products.some(p => (p._id || p.id) === id)));
    }, [products]);

    // Fetch products from API
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const currentUser = authService.getUser() || JSON.parse(localStorage.getItem('user') || 'null');
            const isSuperadmin = currentUser?.role === 'superadmin' || localStorage.getItem('userRole') === 'superadmin';
            const defaultLimit = isSuperadmin ? 0 : 100;

            const filters = {
                search: searchTerm || undefined,
                category: categoryFilter !== 'all' ? categoryFilter : undefined,
                limit: defaultLimit // Superadmin: 0 => no limit, others: 100
            };
            if (selectedLocation) {
                if (typeof selectedLocation === 'object') {
                    if (selectedLocation.city) filters.city = selectedLocation.city;
                    if (selectedLocation.state) filters.state = selectedLocation.state;
                    if (selectedLocation.country) filters.country = selectedLocation.country;
                    if (selectedLocation.area) filters.area = selectedLocation.area;
                } else {
                    filters.location = selectedLocation;
                }
            }
            // Only add status filter when not 'all'
            if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
            // Only set isActive when explicitly looking for approved/live products
            if (statusFilter === 'approved') filters.isActive = true;

            // Remove undefined filters
            Object.keys(filters).forEach(key => filters[key] === undefined && delete filters[key]);

            const response = await productService.getAdminProducts(filters);
            const fetchedProducts = response.products || [];
            setProducts(fetchedProducts);

            // Don't update categories from products - use backend categories instead

            // Calculate stats - always fetch fresh counts in parallel for better performance
            const statsFiltersBase = selectedLocation ? { location: selectedLocation } : {};
            const [totalResult, pendingResult, approvedResult, rejectedResult] = await Promise.all([
                productService.getAdminProducts({ ...statsFiltersBase, limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase, status: 'pending', limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase, status: 'approved', isActive: true, limit: 1 }), // Only count active as approved/live
                productService.getAdminProducts({ ...statsFiltersBase, status: 'rejected', limit: 1 })
            ]);

            setStats({
                total: totalResult.pagination?.total || 0,
                pending: pendingResult.pagination?.total || 0,
                approved: approvedResult.pagination?.total || 0,
                rejected: rejectedResult.pagination?.total || 0
            });
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error(error.message || 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    // Fetch categories from backend
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoadingCategories(true);
                const result = await categoryService.getAllActiveCategoriesFlat();
                const categoriesData = result.categories || result || [];
                const categoryNames = categoriesData
                    .map(cat => cat.name)
                    .filter(name => name && name.trim() !== '')
                    .sort();
                setCategories(categoryNames);
            } catch (error) {
                console.error('Failed to fetch categories:', error);
                // Fallback: extract from products if API fails
                if (products.length > 0) {
                    const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))];
                    setCategories(uniqueCategories.sort());
                }
            } finally {
                setLoadingCategories(false);
            }
        };
        fetchCategories();
    }, []);

    // Fetch products when status or category filter changes
    useEffect(() => {
        fetchProducts();
    }, [statusFilter, categoryFilter, selectedLocation]); // Refetch when status, category or location changes

    // Handle approve product
    const handleApprove = async (productId) => {
        try {
            const response = await productService.approveProduct(productId);
            console.log('Approve response:', response);
            
            // Remove the product from current list immediately (optimistic update)
            setProducts(prevProducts => prevProducts.filter(p => (p._id || p.id) !== productId));
            
            // Refresh stats immediately to update counts
            const statsFiltersBase2 = selectedLocation ? { location: selectedLocation } : {};
            const [totalResult, pendingResult, approvedResult, rejectedResult] = await Promise.all([
                productService.getAdminProducts({ ...statsFiltersBase2, limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase2, status: 'pending', limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase2, status: 'approved', limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase2, status: 'rejected', limit: 1 })
            ]);

            setStats({
                total: totalResult.pagination?.total || 0,
                pending: pendingResult.pagination?.total || 0,
                approved: approvedResult.pagination?.total || 0,
                rejected: rejectedResult.pagination?.total || 0
            });
            
            // If currently viewing pending, switch to approved and fetch
            if (statusFilter === 'pending') {
                // Fetch approved products first
                const approvedFilters = {
                    search: searchTerm || undefined,
                    category: categoryFilter !== 'all' ? categoryFilter : undefined,
                    status: 'approved',
                    limit: 100
                };
                Object.keys(approvedFilters).forEach(key => approvedFilters[key] === undefined && delete approvedFilters[key]);
                const approvedResponse = await productService.getAdminProducts({ ...approvedFilters, ...(selectedLocation ? { location: selectedLocation } : {}) });
                setProducts(approvedResponse.products || []);
                // Then switch tab
                setStatusFilter('approved');
            } else {
                // Refresh products list for current filter
                await fetchProducts();
            }
            
            toast.success('Product approved successfully!');
        } catch (error) {
            console.error('Error approving product:', error);
            toast.error(error.message || 'Failed to approve product');
            // Re-fetch on error to restore correct state
            await fetchProducts();
        }
    };

    // Handle reject product
    const handleReject = async (productId) => {
        if (!window.confirm('Are you sure you want to reject this product? It will be marked as rejected.')) {
            return;
        }
        try {
            await productService.rejectProduct(productId);
            toast.success('Product rejected successfully!');
            
            // Remove the product from current list immediately (optimistic update)
            setProducts(prevProducts => prevProducts.filter(p => (p._id || p.id) !== productId));
            
            // Refresh stats immediately to update counts
            const statsFiltersBase3 = selectedLocation ? { location: selectedLocation } : {};
            const [totalResult, pendingResult, approvedResult, rejectedResult] = await Promise.all([
                productService.getAdminProducts({ ...statsFiltersBase3, limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase3, status: 'pending', limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase3, status: 'approved', limit: 1 }),
                productService.getAdminProducts({ ...statsFiltersBase3, status: 'rejected', limit: 1 })
            ]);

            setStats({
                total: totalResult.pagination?.total || 0,
                pending: pendingResult.pagination?.total || 0,
                approved: approvedResult.pagination?.total || 0,
                rejected: rejectedResult.pagination?.total || 0
            });
            
            // Refresh products list for current filter
            await fetchProducts();
        } catch (error) {
            console.error('Error rejecting product:', error);
            toast.error(error.message || 'Failed to reject product');
            // Re-fetch on error to restore correct state
            await fetchProducts();
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;
        try {
            await productService.deleteAdminProduct(productId);
            toast.success('Product deleted successfully');
            setSelectedProductIds(prev => prev.filter(id => id !== productId));
            await fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error(error.message || 'Failed to delete product');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedProductIds.length === 0) return;
        if (!window.confirm(`Delete ${selectedProductIds.length} selected product(s)?`)) return;
        setBulkDeleting(true);
        try {
            await Promise.all(selectedProductIds.map((id) => productService.deleteAdminProduct(id)));
            toast.success(`${selectedProductIds.length} product(s) deleted successfully`);
            setSelectedProductIds([]);
            await fetchProducts();
        } catch (error) {
            console.error('Error deleting selected products:', error);
            toast.error(error.message || 'Failed to delete selected products');
        } finally {
            setBulkDeleting(false);
        }
    };

    const startEditProduct = (product) => {
        setEditingProduct(product);
        setEditForm({
            name: product.name || '',
            sku: product.sku || '',
            category: product.category || '',
            price: Number(product.price || 0),
            stock: Number(product.stock || 0),
            status: product.status || 'pending',
            isActive: Boolean(product.isActive),
            description: product.description || ''
        });
    };

    const handleEditSave = async () => {
        if (!editingProduct) return;
        try {
            setSavingEdit(true);
            const productId = editingProduct._id || editingProduct.id;
            await productService.updateAdminProduct(productId, {
                ...editForm,
                price: Number(editForm.price),
                stock: Number(editForm.stock)
            });
            toast.success('Product updated successfully');
            setEditingProduct(null);
            await fetchProducts();
        } catch (error) {
            console.error('Error updating product:', error);
            toast.error(error.message || 'Failed to update product');
        } finally {
            setSavingEdit(false);
        }
    };

    const toggleSelectProduct = (productId) => {
        setSelectedProductIds(prev =>
            prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
        );
    };

    const toggleSelectAllVisible = () => {
        const visibleIds = filteredProducts.map(p => p._id || p.id);
        const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedProductIds.includes(id));
        if (allSelected) {
            setSelectedProductIds(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            setSelectedProductIds(prev => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const handleAction = async (type, product) => {
        if (type === 'view') {
            try {
                setLoading(true);
                // Fetch detailed product info including reviews/rating
                const id = product._id || product.id;
                const data = await productService.getAdminProductById(id);
                const detailed = data.product || data;
                // Attach reviews and rating if present
                detailed.rating = data.rating ?? detailed.rating ?? 0;
                detailed.reviewCount = data.reviewCount ?? detailed.reviewCount ?? (data.reviews?.length || 0);
                detailed.reviews = data.reviews || [];
                setSelectedProduct(detailed);
                setView('view_detail');
            } catch (error) {
                console.error('Failed to fetch product details:', error);
                toast.error(error.message || 'Failed to load product details');
            } finally {
                setLoading(false);
            }
        } else {
            setSelectedProduct(product);
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = !searchTerm ||
            product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.vendorId?.name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;

        return matchesSearch && matchesCategory;
    });

    // Get inventory status based on stock levels (for approved products)
    const getInventoryStatus = (product) => {
        if (product.status !== 'approved') {
            return null; // Only show inventory status for approved products
        }
        const stock = product.stock || 0;
        if (stock === 0) {
            return { label: 'OUT OF STOCK', style: 'bg-red-100 text-red-800', icon: <FaTimesCircle className="text-xs" /> };
        } else if (stock < 20) {
            return { label: 'LOW STOCK', style: 'bg-orange-100 text-orange-800', icon: <FaExclamationTriangle className="text-xs" /> };
        } else {
            return { label: 'ACTIVE', style: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="text-xs" /> };
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'approved': return <FaCheckCircle className="text-xs" />;
            case 'pending': return <FaClock className="text-xs" />;
            case 'rejected': return <FaTimesCircle className="text-xs" />;
            default: return null;
        }
    };

    const getVendorName = (product) => {
        // Prefer vendorId, fallback to userId for legacy/alternate creators (mechanic/garage)
        const v = product.vendorId;
        const u = product.userId;
        // If vendorId is an object, prefer vendorDetails.businessName, then name, then email
        if (v && typeof v === 'object') {
            if (v.vendorDetails && (v.vendorDetails.businessName || v.vendorDetails.name)) return v.vendorDetails.businessName || v.vendorDetails.name;
            if (v.name) return v.name;
            if (v.email) return v.email;
        }
        // If vendorId is a string id, try userId object next
        if (u && typeof u === 'object') {
            if (u.vendorDetails && (u.vendorDetails.businessName || u.vendorDetails.name)) return u.vendorDetails.businessName || u.vendorDetails.name;
            if (u.name) return u.name;
            if (u.email) return u.email;
        }
        if (typeof v === 'string') return v;
        if (typeof u === 'string') return u;
        return 'Unknown Vendor';
    };

    const getReviewerName = (review) => {
        if (!review) return 'Anonymous';
        if (review.userName) return review.userName;
        const uid = review.userId;
        if (!uid) return 'Anonymous';
        // If populated as object, prefer name then email
        if (typeof uid === 'object') {
            return uid.name || uid.email || 'Anonymous';
        }
        // If it's a string id, return 'User' placeholder or the id trimmed
        return typeof uid === 'string' && uid.length > 0 ? uid : 'Anonymous';
    };

    if (view === 'view_detail' && selectedProduct) {
        return (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight">Product Details</h2>
                    <button onClick={() => setView('list')} className="text-gray-500 font-bold hover:text-gray-900">← Back</button>
                </div>
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Left: Image + Rating & Reviews */}
                    <div className="space-y-4">
                        <div className="w-full aspect-[4/3] bg-gray-50 rounded-2xl flex items-center justify-center text-gray-200 overflow-hidden">
                            {selectedProduct.images && selectedProduct.images.length > 0 ? (
                                <img loading="lazy" src={selectedProduct.images[0]} alt={selectedProduct.name} className="w-full h-full object-contain" />
                            ) : selectedProduct.imageUrl ? (
                                <img loading="lazy" src={selectedProduct.imageUrl} alt={selectedProduct.name} className="w-full h-full object-contain" />
                            ) : (
                                <FaBox size={100} />
                            )}
                        </div>

                        {/* Rating & Reviews (moved under image) */}
                        <div>
                            <div className="flex items-center gap-4">
                                <div className="text-yellow-400 font-black text-2xl">
                                    {(selectedProduct.rating || 0).toFixed(1)}
                                </div>
                                <div className="flex items-center gap-1 text-yellow-400">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <span key={i} className="text-sm">
                                            {i < Math.round(selectedProduct.rating || 0) ? '★' : '☆'}
                                        </span>
                                    ))}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {selectedProduct.reviewCount || (selectedProduct.reviews?.length || 0)} review{(selectedProduct.reviewCount || selectedProduct.reviews?.length || 0) !== 1 ? 's' : ''}
                                </div>
                            </div>

                            <div className="mt-4 space-y-3 max-h-64 overflow-y-auto pr-2">
                                {selectedProduct.reviews && selectedProduct.reviews.length > 0 ? (
                                    selectedProduct.reviews.slice(0, 10).map((r) => (
                                        <div key={r._id || r.id} className="p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div className="font-bold text-sm">{getReviewerName(r)}</div>
                                                <div className="text-sm text-yellow-400 font-black">{r.rating} ★</div>
                                            </div>
                                            <p className="text-sm text-gray-700 mt-2">{r.text}</p>
                                            {r.createdAt && <p className="text-xs text-gray-400 mt-2">{new Date(r.createdAt).toLocaleString()}</p>}
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">No reviews yet for this product.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right: Full metadata & actions (details preserved) */}
                    <div className="space-y-6">
                        <div>
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${getStatusStyle(selectedProduct.status)}`}>
                                {selectedProduct.status || 'pending'}
                            </span>
                            <h3 className="text-3xl font-black text-gray-900 mt-2">{selectedProduct.name}</h3>
                            <p className="text-gray-500 font-bold">SKU: {selectedProduct.sku || 'N/A'}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-gray-50 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase">Vendor</p>
                                <p className="font-bold text-gray-900">{getVendorName(selectedProduct)}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase">Category</p>
                                <p className="font-bold text-gray-900">{selectedProduct.category || 'N/A'}</p>
                            </div>
                            <div className="p-4 bg-gray-100 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase">Price</p>
                                <p className="text-xl font-black text-blue-600">₹{selectedProduct.price?.toLocaleString() || '0'}</p>
                            </div>
                            <div className="p-4 bg-gray-100 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase">Stock Level</p>
                                <p className="text-xl font-black text-gray-900">{selectedProduct.stock?.toLocaleString() || '0'} Units</p>
                            </div>
                        </div>

                        {/* Additional Product Details */}
                        <div className="grid grid-cols-2 gap-4">
                            {selectedProduct.brand && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Brand</p>
                                    <p className="font-bold text-gray-900">{selectedProduct.brand}</p>
                                </div>
                            )}
                            {selectedProduct.partNumber && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Part Number</p>
                                    <p className="font-bold text-gray-900">{selectedProduct.partNumber}</p>
                                </div>
                            )}
                            {selectedProduct.origin && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Origin</p>
                                    <p className="font-bold text-gray-900">{selectedProduct.origin}</p>
                                </div>
                            )}
                            {selectedProduct.class && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Class</p>
                                    <p className="font-bold text-gray-900">{selectedProduct.class}</p>
                                </div>
                            )}
                            {selectedProduct.mrp && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">MRP</p>
                                    <p className="font-bold text-gray-900">₹{selectedProduct.mrp.toLocaleString()}</p>
                                </div>
                            )}
                            {selectedProduct.discount && selectedProduct.discount > 0 && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Discount</p>
                                    <p className="font-bold text-green-600">-{selectedProduct.discount}%</p>
                                </div>
                            )}
                            {selectedProduct.minOrderQty && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Min Order Qty</p>
                                    <p className="font-bold text-gray-900">{selectedProduct.minOrderQty} Units</p>
                                </div>
                            )}
                            {selectedProduct.soldBy && (
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[10px] font-black text-gray-400 uppercase">Sold By</p>
                                    <p className="font-bold text-gray-900">{selectedProduct.soldBy}</p>
                                </div>
                            )}
                        </div>

                        {selectedProduct.vehicleCompatibility && selectedProduct.vehicleCompatibility.length > 0 && (
                            <div className="p-4 bg-gray-50 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Vehicle Compatibility</p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedProduct.vehicleCompatibility.map((vehicle, index) => {
                                        const label = typeof vehicle === 'object' && vehicle !== null
                                            ? [vehicle.make, vehicle.model, vehicle.variant !== 'All' ? vehicle.variant : '', vehicle.yearFrom, vehicle.yearTo ? `- ${vehicle.yearTo}` : ''].filter(Boolean).join(' ')
                                            : String(vehicle);
                                        return (
                                            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs font-bold">
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {selectedProduct.warranty && (
                            <div className="p-4 bg-gray-50 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase">Warranty</p>
                                <p className="font-bold text-gray-900">{selectedProduct.warranty}</p>
                            </div>
                        )}
                        {selectedProduct.deliveryTime && (
                            <div className="p-4 bg-gray-50 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase">Delivery Time</p>
                                <p className="font-bold text-gray-900">{selectedProduct.deliveryTime}</p>
                            </div>
                        )}
                        {selectedProduct.description && (
                            <div className="p-4 bg-gray-50 rounded-2xl">
                                <p className="text-[10px] font-black text-gray-400 uppercase mb-2">Description</p>
                                <p className="text-sm text-gray-700">{selectedProduct.description}</p>
                            </div>
                        )}

                        {/* Approve/Reject actions */}
                        {selectedProduct.status === 'pending' && (
                            <div className="flex gap-4">
                                <button 
                                    onClick={async () => {
                                        await handleApprove(selectedProduct._id || selectedProduct.id);
                                        setView('list');
                                    }} 
                                    className="flex-1 py-4 bg-green-600 text-white font-black rounded-2xl uppercase tracking-widest hover:bg-green-700 transition"
                                >
                                    Approve Product
                                </button>
                                <button 
                                    onClick={async () => {
                                        await handleReject(selectedProduct._id || selectedProduct.id);
                                        setView('list');
                                    }} 
                                    className="px-6 py-4 border border-red-200 text-red-600 font-black rounded-2xl uppercase tracking-widest hover:bg-red-50 transition"
                                >
                                    Reject
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Products</h2>
                    <p className="text-sm text-slate-500 mt-0.5">Review and manage product listings</p>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={fetchProducts}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-bold flex items-center gap-2 hover:bg-gray-50 transition shadow-sm"
                    >
                        <FaDownload /> Refresh
                    </button>
                    <div className="hidden md:flex items-center">
                        <AdminLocationDropdown
                            value={selectedLocation}
                            onChange={(v) => {
                                setSelectedLocation(v);
                                try {
                                    if (!v) {
                                        localStorage.removeItem('adminLocation');
                                    } else if (typeof v === 'object') {
                                        localStorage.setItem('adminLocation', JSON.stringify(v));
                                    } else {
                                        localStorage.setItem('adminLocation', String(v));
                                    }
                                } catch (e) {}
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <p className="text-xs font-medium text-slate-500 mb-0.5">Total Products</p>
                    <p className="text-2xl font-bold text-blue-600 tabular-nums">{stats.total.toLocaleString()}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <p className="text-xs font-medium text-slate-500 mb-0.5">Pending Review</p>
                    <p className="text-2xl font-bold text-amber-600 tabular-nums">{stats.pending.toLocaleString()}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <p className="text-xs font-medium text-slate-500 mb-0.5">Approved (Live)</p>
                    <p className="text-2xl font-bold text-emerald-600 tabular-nums">{stats.approved.toLocaleString()}</p>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                    <p className="text-xs font-medium text-slate-500 mb-0.5">Rejected</p>
                    <p className="text-2xl font-bold text-red-600 tabular-nums">{stats.rejected.toLocaleString()}</p>
                </div>
            </div>

            {/* Tabs for Status Filter */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-1.5">
                <div className="flex flex-wrap gap-1.5">
                    <button
                        onClick={() => {
                            setStatusFilter('approved');
                            setSearchTerm('');
                            setCategoryFilter('all');
                        }}
                        className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg text-sm font-semibold transition ${
                            statusFilter === 'approved'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                        <FaCheckCircle className="inline mr-1.5 text-xs" />
                        Live ({stats.approved})
                    </button>
                    <button
                        onClick={() => {
                            setStatusFilter('all');
                            setSearchTerm('');
                            setCategoryFilter('all');
                        }}
                        className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg text-sm font-semibold transition ${
                            statusFilter === 'all'
                                ? 'bg-blue-100 text-blue-800'
                                : 'text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                        <FaBox className="inline mr-1.5 text-xs" />
                        All ({stats.total})
                    </button>
                    <button
                        onClick={() => {
                            setStatusFilter('pending');
                            setSearchTerm('');
                            setCategoryFilter('all');
                        }}
                        className={`flex-1 min-w-[140px] px-3 py-2 rounded-lg text-sm font-semibold transition ${
                            statusFilter === 'pending'
                                ? 'bg-amber-100 text-amber-800'
                                : 'text-slate-600 hover:bg-slate-50'
                        }`}
                    >
                        <FaClock className="inline mr-1.5 text-xs" />
                        Pending ({stats.pending})
                    </button>
                </div>
            </div>

            {/* Filters Area */}
            <div className="dashboard-filters !p-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 flex-1 w-full">
                    <div className="md:col-span-2 relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 text-sm" />
                        <input
                            type="text"
                            placeholder="Search by Product Name, SKU, or Vendor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') fetchProducts();
                            }}
                            className="dashboard-input w-full pl-9 pr-20 py-2 text-sm"
                        />
                        <button
                            type="button"
                            onClick={fetchProducts}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-xs font-semibold"
                        >
                            Search
                        </button>
                    </div>
                    <div className="relative">
                        <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="dashboard-select w-full pl-9 py-2 text-sm"
                            disabled={loadingCategories}
                        >
                            <option value="all">All Categories</option>
                            {loadingCategories ? (
                                <option disabled>Loading categories…</option>
                            ) : (
                                categories.map(cat => <option key={cat} value={cat}>{cat}</option>)
                            )}
                        </select>
                        {loadingCategories && (
                            <FaSpinner className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin text-sm" />
                        )}
                    </div>
                </div>
            </div>

            {/* Inventory Table */}
            <div className="dashboard-card overflow-hidden">
                {loading ? (
                    <div className="py-16 text-center">
                        <FaSpinner className="animate-spin text-3xl text-blue-600 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">Loading products…</p>
                    </div>
                ) : (
                    <>
                        {selectedProductIds.length > 0 && (
                            <div className="px-4 py-2 border-b border-slate-100 bg-red-50 flex items-center justify-between">
                                <p className="text-sm font-medium text-red-700">
                                    {selectedProductIds.length} selected
                                </p>
                                <button
                                    onClick={handleBulkDelete}
                                    disabled={bulkDeleting}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50"
                                >
                                    {bulkDeleting ? 'Deleting…' : 'Delete selected'}
                                </button>
                            </div>
                        )}
                        <div className="dashboard-table-wrap">
                            <table className="dashboard-table [&_th]:py-2.5 [&_td]:py-2">
                                <thead>
                                    <tr>
                                        <th className="w-10">
                                            <input
                                                type="checkbox"
                                                onChange={toggleSelectAllVisible}
                                                checked={
                                                    filteredProducts.length > 0 &&
                                                    filteredProducts.every(p => selectedProductIds.includes(p._id || p.id))
                                                }
                                            />
                                        </th>
                                        <th>Product & SKU</th>
                                        <th>Vendor</th>
                                        <th>Category</th>
                                        <th className="text-center">Price</th>
                                        <th className="text-center">Stock</th>
                                        <th className="text-center">Status</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProducts.map(product => (
                                        <tr key={product._id || product.id} className="group">
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProductIds.includes(product._id || product.id)}
                                                    onChange={() => toggleSelectProduct(product._id || product.id)}
                                                />
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-2.5 min-w-[180px]">
                                                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 shrink-0">
                                                        <FaBox className="text-sm" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-slate-900 truncate leading-snug">{product.name}</p>
                                                        <p className="text-[11px] font-mono text-slate-400 truncate">{product.sku || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flex items-center gap-1.5 text-sm text-slate-600 max-w-[140px]">
                                                    <FaStore className="text-slate-300 shrink-0 text-xs" />
                                                    <span className="truncate">{getVendorName(product)}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="badge-neutral text-[10px]">
                                                    {product.category || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="text-center text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                                                ₹{product.price?.toLocaleString() || '0'}
                                            </td>
                                            <td className="text-center text-sm font-medium text-slate-700 tabular-nums">
                                                {product.stock?.toLocaleString() || '0'}
                                            </td>
                                            <td className="text-center">
                                                {(() => {
                                                    const inventoryStatus = getInventoryStatus(product);
                                                    const badgeClass = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap';
                                                    if (inventoryStatus) {
                                                        return (
                                                            <span className={`${badgeClass} ${inventoryStatus.style}`}>
                                                                {inventoryStatus.icon}
                                                                {inventoryStatus.label}
                                                            </span>
                                                        );
                                                    }
                                                    return (
                                                        <span className={`${badgeClass} ${getStatusStyle(product.status || 'pending')}`}>
                                                            {getStatusIcon(product.status || 'pending')}
                                                            {product.status || 'pending'}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            <td className="text-right">
                                                <div className="flex items-center justify-end gap-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAction('view', product)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition"
                                                        title="View"
                                                    >
                                                        <FaEye size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => startEditProduct(product)}
                                                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition"
                                                        title="Edit"
                                                    >
                                                        <FaEdit size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteProduct(product._id || product.id)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition"
                                                        title="Delete"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                    {product.status === 'pending' && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleApprove(product._id || product.id)}
                                                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition"
                                                                title="Approve"
                                                            >
                                                                <FaCheckCircle size={14} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleReject(product._id || product.id)}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-md transition"
                                                                title="Reject"
                                                            >
                                                                <FaTimesCircle size={14} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {filteredProducts.length === 0 && !loading && (
                            <div className="dashboard-empty py-12">
                                <div className="dashboard-empty-icon">
                                    <FaBox className="text-2xl" />
                                </div>
                                <p className="dashboard-empty-title">No products found</p>
                                <p className="dashboard-empty-desc">Try adjusting your search or filters.</p>
                            </div>
                        )}
                    </>
                )}
            </div>

            {editingProduct && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-black text-gray-900">Edit Product</h3>
                            <button
                                type="button"
                                onClick={() => setEditingProduct(null)}
                                className="text-gray-500 font-bold hover:text-gray-900"
                            >
                                Close
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input className="px-4 py-3 bg-gray-50 rounded-xl font-bold" placeholder="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                            <input className="px-4 py-3 bg-gray-50 rounded-xl font-bold" placeholder="SKU" value={editForm.sku} onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })} />
                            <input className="px-4 py-3 bg-gray-50 rounded-xl font-bold" placeholder="Category" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
                            <select className="px-4 py-3 bg-gray-50 rounded-xl font-bold" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                                <option value="pending">pending</option>
                                <option value="approved">approved</option>
                                <option value="rejected">rejected</option>
                            </select>
                            <input type="number" className="px-4 py-3 bg-gray-50 rounded-xl font-bold" placeholder="Price" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
                            <input type="number" className="px-4 py-3 bg-gray-50 rounded-xl font-bold" placeholder="Stock" value={editForm.stock} onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })} />
                        </div>

                        <textarea
                            rows={3}
                            className="w-full px-4 py-3 bg-gray-50 rounded-xl font-bold"
                            placeholder="Description"
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        />

                        <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                            <input
                                type="checkbox"
                                checked={editForm.isActive}
                                onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                            />
                            Active product
                        </label>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setEditingProduct(null)}
                                className="px-4 py-2 rounded-lg border border-gray-200 font-bold"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleEditSave}
                                disabled={savingEdit}
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-bold disabled:opacity-50"
                            >
                                {savingEdit ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Internal Security Note */}
            <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-2xl border border-yellow-200">
                <FaExclamationTriangle className="text-yellow-600 shrink-0" />
                <p className="text-[10px] font-bold text-yellow-800 uppercase leading-relaxed">
                    Admin Notice: All product approval actions are logged. Only approved products are visible to customers on the public website.
                </p>
            </div>
        </div>
    );
};

export default Products;








